import { ViewItemType } from '../analytics/datalayer/types'

export type WebEngageOptionKey = 'isGzip' | 'proxyUrl' | 'webpush.registerServiceWorker'

export type WindowWithWebengage = Window & {
    webengage?: {
        user: {
            logout: () => void
            login: (userId: string) => void
            setAttribute: (key: string, value: string | number | Date) => void
        }
        track: (event: string, data: object) => void
        options: (key: WebEngageOptionKey, value: boolean | string) => void
    }
}

export interface WebengageEventPayloadMap {
    [SupportedEvents.homepage_view]: TrackingDataType
    [SupportedEvents.page_view]: TrackingDataType
    [SupportedEvents.search]: { search_term: string | string[] }
    [SupportedEvents.view_item]: PdpType
    [SupportedEvents.product_review_submitted]: SubmitedReviewType
    [SupportedEvents.coupon_code_failed]: { coupon_code: string; failure_reason: string }
    [SupportedEvents.view_cart]: {
        value: number
        overall_quantity: number
        item_id: string
        item_name: string
        price: string
        discount: string
        item_brand: string
        item_variant?: string
        item_category1?: string
        item_category2?: string
        item_category3?: string
    }
    [SupportedEvents.begin_checkout]: {
        value: number
        overall_quantity: number
        item_id: string
        item_name: string
        price: string
        discount: string
        item_brand: string
        item_variant: string
        item_category1: string
        item_category2: string
        item_category3: string
        coupon?: string
        coupon_amount?: number
    }
    [SupportedEvents.add_shipping_info]: {
        shipping_tier?: string
        shipping_price: number
        shipping_discount: number
        delivery_date: Date
        shipment_count: number
    }
    [SupportedEvents.add_to_favorite]: WishlistType
    [SupportedEvents.remove_from_favorite]: WishlistType
    [SupportedEvents.add_payment_info]: {
        payment_type?: string
        wallet_credit: number
    }
    [SupportedEvents.purchase]: {
        item_id: string
        item_name: string
        price: string
        discount: string
        item_brand: string
        item_variant?: string
        item_category1?: string
        item_category2?: string
        item_category3?: string
        coupon?: string
        coupon_amount?: number
        transaction_id: string
        order_id: string
        payment_type: string
        shipping_price: number
    }
    [SupportedEvents.add_to_cart]: CartProductType
    [SupportedEvents.remove_from_cart]: CartProductType
    [SupportedEvents.cta_click]: ActionType
}

export type TrackPageViewType = {
    pathname: string | null
    pageTitle: string | null
    prevPath: string | null
}

export type TrackingDataType = {
    page_url: string | null
    page_title?: string | null
    page_referrer?: string | null
}

export type CartProductType = {
    discount: number
    item_id: string | null
    item_name: string
    price: number | string
    item_brand: string | null
    item_category1?: string | null
    item_category2?: string | null
    item_category3?: string | null
    quantity: number | string
    item_variant?: string | null
    item_list_name: CartActionPlacement
    item_list_id: CartActionPlacement
    coupon?: null | 'pricinggift'
    ship_by_seller: boolean
}

export const WhishlistActionType = {
    add_to_wishlist: 'add_to_wishlist',
    remove_from_wishlist: 'remove_from_wishlist',
} as const

export type WhishlistActionType =
    | keyof typeof WhishlistActionType
    | (typeof WhishlistActionType)[keyof typeof WhishlistActionType]

export type WishlistType = {
    product_id: string
    product_name: string
    product_brand: string
    item_category1?: string
    item_category2?: string
    item_category3?: string
}

export type PdpType = {
    item_id: string
    item_name: string
    price: string
    discount: string
    item_brand: string
    item_variant?: string
    item_category1?: string
    item_category2?: string
    item_category3?: string
    item_list_id: ViewItemType
    item_list_name: ViewItemType
    ship_by_seller: string
}

export type SubmitedReviewType = {
    product_id: string
    product_name: string
    item_brand: string
    rating: number
    item_category1?: string
    item_category2?: string
    item_category3?: string
}

export enum KeyPathType {
    id = 'id',
    discountPrice = 'discountPrice',
    innerColorName = 'color.name',
    atpId = 'atpId',
    productId = 'productId',
    productName = 'productName',
    salesPrice = 'salesPrice',
    discount = 'discount',
    brandName = 'brandName',
    colorName = 'colorName',
    categoryLevel1 = 'categoryLevel1',
    categoryLevel2 = 'categoryLevel2',
    categoryLevel3 = 'categoryLevel3',
    itemVariant = 'variant',
    shipBySeller = 'shipBySeller',
}

export enum SupportedEvents {
    homepage_view = 'homepage_view',
    page_view = 'page_view',
    search = 'search',
    add_to_favorite = 'add_to_favorite',
    remove_from_favorite = 'remove_from_favorite',
    view_item = 'view_item',
    product_review_submitted = 'product_review_submitted',
    coupon_code_failed = 'coupon_code_failed',
    view_cart = 'view_cart',
    add_shipping_info = 'add_shipping_info',
    begin_checkout = 'begin_checkout',
    purchase = 'purchase',
    add_payment_info = 'add_payment_info',
    add_to_cart = 'add_to_cart',
    remove_from_cart = 'remove_from_cart',
    cta_click = 'cta_click',
}

export enum StorageKey {
    CurrentPath = 'currentPath',
    PrevPath = 'prevPath',
    Base = 'base',
}

export enum CartActionPlacement {
    pdp = 'pdp',
    cart = 'cart',
    pop_in_cart = 'pop_in_cart',
    video = 'video',
}

export type ActionType = {
    banner_type: string
    banner_name: string
    banner_index: number | string
    url_destination: string
    item_id: string
    component_id: string
    image_url?: string
}
